import { useState } from "react"
import { Spinner } from "@fluentui/react"
import { errorToString, getBrowserFingerprintData } from "@oneethos/shared"
import { useApi } from "../../hooks"
import api from "../../api-client"
import { toast } from "react-toastify"
import { SubformProps } from "."
import { lappToFormData } from "./util"
import { Modal } from '../../components/modal'
import { MustClickLink } from "../../components"

type PrequalModalProps = Partial<SubformProps> & {
  letterData: any
  onClose: () => void
}

const PrequalModal = ({ formData, update, save, letterData, onClose }: PrequalModalProps) => {
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(false)
  const [agreement, setAgreement] = useState(formData.preapprovalLetter?.agreement)
  const acked = formData.preapprovalLetter?.agreement?.agrees === true

  const handleDownload = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      window.open(letterData.url, '_blank')
    } catch (error) {
      toast.error(errorToString(error))
    } finally {
      setLoading(false)
    }
  }

  return <Modal
    onClose={onClose}
    containerStyles={{ maxWidth: '1000px' }}
    dismissable={acked}
  >
    <div>
      <object
        data={letterData.url}
        type="application/pdf"
        title={`Prequalification Letter for ${formData._id}`}
        style={{
          width: '100%',
          height: '500px',
          border: 'none',
          marginTop: '20px'
        }}
      >
        <p>
          Unable to display PDF file.
          {loading ? (
            <span>Loading...</span>
          ) : (
            <a href="#" onClick={handleDownload}> Download</a>
          )} instead.
        </p>
      </object>
      {saving ? <Spinner style={{ padding: '2em' }} /> : (
        acked ? (
          <>
            <div className="alert alert-info mt-3">
              Thank you for acknowledging receipt of the prequalification letter. You should have
              received a copy via email, or you may download the copy above for your records.
            </div>
          </>
        ) : <div className={'form-group'} style={{ cursor: 'pointer', textAlign: 'left' }}>
          <label style={{ margin: '1em 0' }}>
            <input type="checkbox"
              onChange={ev => {
                if (ev.target.checked) {
                  setAgreement({
                    browser: getBrowserFingerprintData(),
                    agrees: ev.target.checked
                  })
                } else {
                  setAgreement(null)
                }
              }}
              checked={agreement?.agrees === true}
            />
            &nbsp;
            I acknowledge that I have reviewed the above prequalification letter; by clicking
            "Accept", it will record my acknowledgement and email me a copy of this agreement.
          </label>
          <button type="button"
            className="btn btn-primary"
            disabled={agreement?.agrees !== true}
            onClick={() => {
              setSaving(true)
              api.patch(`/c/loanapps/${formData._id}/prequalification-letter`, {
                contentMD5: letterData.contentMD5,
                agreement: {
                  ...agreement,
                  timestamp: new Date(),
                }
              }).then(res => update(lappToFormData(res)))
                .catch(ex => toast.error(errorToString(ex)))
                .finally(() => { setSaving(false) })
            }}>Accept</button>
        </div>
      )}
    </div>
  </Modal>
}

export const PrequalLetter = ({ formData, update, save }: Partial<SubformProps>) => {
  const [showModal, setShowModal] = useState(false)

  const { fetching, error, data } = useApi(`/c/loanapps/${formData._id}/prequalification-letter`)

  if (fetching) {
    return <Spinner />
  }

  const expired = error && typeof error !== 'string' && error.code === 'PREQUAL_EXPIRED'

  if (error && typeof error === 'string') {
    return <div className="alert alert-danger">{error}</div>
  } else if (error && !expired) {
    return <div className="alert alert-danger">
      {errorToString(error)}
    </div>
  }

  return <div className="prequal-letter">
    {expired ? <div className="alert alert-info">
      Your prequalification has expired; please contact the bank to discuss options.
    </div> : <div>
      <p>
        The following link includes the details of your prequalification. You must review and
        acknowledge the letter to continue. Upon acceptance, you will be emailed
        a copy for your records. Your prequalification is valid for 30 days.
      </p>

      <MustClickLink
        label="Review Prequalification Letter"
        onClicked={() => setShowModal(true)}
        clicked={formData.preapprovalLetter?.agreement?.agrees}
      />

      {showModal ? <PrequalModal
        formData={formData}
        update={update}
        save={save}
        letterData={data}
        onClose={() => setShowModal(false)}
      /> : null}
    </div>}
  </div>
}

